import { BasicDisplayUnits, additionalUnitLabels, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import {
  CustomUnitFields,
  DisplayUnits,
  MetricPressureUnit,
  UnitSystem
} from 'providers/api';
import { round } from 'utils';
import {
  densityMetricToImperial,
  distanceMetricToImperial,
  flowRateMetricToImperial,
  pressureMetricToImperial,
  pumpPressureBarToKpa,
  pumpPressureMetricToImperial,
  speedMetricToImperial,
  tonnageMetricToImperial,
  isCustomUnitField,
  volumeMetricToImperial,
  gravityMetricToImperial
} from 'utils/unitConversions';

export function convertPressure(pressureKpa: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let convertedPressure = pressureKpa;
  let units = unitLabels[BasicDisplayUnits.PressureUCS].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.PressureUCS].customUnitField);

  if (isCustom) {
    convertedPressure = pressureMetricToImperial(convertedPressure);
    units = unitLabels[BasicDisplayUnits.PressureUCS].imperial.abbreviation;
  }
  convertedPressure = round(convertedPressure, 0);
  if (Number.isNaN(convertedPressure)) return `- ${units}`;
  return `${convertedPressure} ${units}`;
}

export function convertPumpPressure(
  pressureBar: number,
  imperial: boolean,
  customUnitFields:CustomUnitFields[],
  metricPressureUnit: MetricPressureUnit = MetricPressureUnit.Bar,
) {
  let convertedPressure = pressureBar;
  let units = unitLabels[BasicDisplayUnits.PressurePump].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.PressurePump].customUnitField);
  const isKiloPascal = !isCustom && metricPressureUnit === MetricPressureUnit.KiloPascal;

  if (isCustom) {
    convertedPressure = pumpPressureMetricToImperial(convertedPressure);
    units = unitLabels[BasicDisplayUnits.PressurePump].imperial.abbreviation;
  } else if (isKiloPascal) {
    convertedPressure = pumpPressureBarToKpa(convertedPressure);
    units = 'kPa';
  }
  convertedPressure = round(convertedPressure, isKiloPascal ? 0 : 1);
  if (Number.isNaN(convertedPressure)) return `- ${units}`;
  return `${convertedPressure} ${units}`;
}

export function convertGravity(gravity: number, slurryDensity: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let convertedGravity = gravity / ((slurryDensity / 1000) * 9.81);
  let units = unitLabels[BasicDisplayUnits.Gravity].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.Gravity].customUnitField);

  if (isCustom) {
    convertedGravity = gravityMetricToImperial(convertedGravity);
    units = unitLabels[BasicDisplayUnits.Gravity].imperial.abbreviation;
  }
  convertedGravity = round(convertedGravity, 1);
  if (Number.isNaN(convertedGravity)) return `- ${units}`;
  return `${convertedGravity} ${units}`;
}

export function convertDistance(_d: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let d = _d;
  let units = unitLabels[BasicDisplayUnits.Distance].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.Distance].customUnitField);
  if (isCustom) {
    d = distanceMetricToImperial(d);
    units = unitLabels[BasicDisplayUnits.Distance].imperial.abbreviation;
  }
  d = round(d, 0);
  if (Number.isNaN(d)) return `- ${units}`;
  return `${d} ${units}`;
}

export function convertSpeed(_s: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let s = _s;
  let units = unitLabels[BasicDisplayUnits.Speed].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.Speed].customUnitField);

  if (isCustom) {
    s = speedMetricToImperial(s);
    units = unitLabels[BasicDisplayUnits.Speed].imperial.abbreviation;
  }
  s = round(s, 1);
  if (Number.isNaN(s)) return `- ${units}`;
  return `${s} ${units}`;
}

export function convertVolume(_v: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let v = _v;
  let units = unitLabels[BasicDisplayUnits.Volume].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.Volume].customUnitField);
  if (isCustom) {
    v = volumeMetricToImperial(v);
    units = unitLabels[BasicDisplayUnits.Volume].imperial.abbreviation;
  }
  v = round(v, 0);
  if (Number.isNaN(v)) return `- ${units}`;
  return `${v} ${units}`;
}

export function convertDensity(_d: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let d = _d;
  let units = unitLabels[BasicDisplayUnits.Density].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.Density].customUnitField);
  if (isCustom) {
    d = densityMetricToImperial(d);
    units = unitLabels[BasicDisplayUnits.Density].imperial.abbreviation;
  }
  d = round(d, 0);
  if (Number.isNaN(d)) return `- ${units}`;
  return `${d} ${units}`;
}

export function convertTonnage(_t: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let t = _t;
  let units = unitLabels[BasicDisplayUnits.DryTonnage].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitLabels[BasicDisplayUnits.DryTonnage].customUnitField);
  if (isCustom) {
    t = tonnageMetricToImperial(t);
    units = unitLabels[BasicDisplayUnits.DryTonnage].imperial.abbreviation;
  }
  t = round(t, 0);
  if (Number.isNaN(t)) return `- ${units}`;
  return `${t} ${units}`;
}

export function convertFlowRate(_f: number, imperial: boolean, customUnitFields:CustomUnitFields[]) {
  let f = _f;
  let units = additionalUnitLabels[DisplayUnits.FlowRate].metric.abbreviation;
  const unitSystem = imperial ? UnitSystem.Imperial : UnitSystem.Metric;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, additionalUnitLabels[DisplayUnits.FlowRate].customUnitField);
  if (isCustom) {
    f = flowRateMetricToImperial(f);
    units = additionalUnitLabels[DisplayUnits.FlowRate].imperial.abbreviation;
  }
  f = round(f, 0);
  if (Number.isNaN(f)) return `- ${units}`;
  return `${f} ${units}`;
}

export function convertkPaToM(pressure: number | undefined, density: number | undefined) {
  if (typeof pressure !== 'undefined' && typeof density !== 'undefined' && density > 0) {
    const result = (pressure * 1000) / (9.81 * density);
    if (!Number.isFinite(result)) return 0;
    return round(result, 0);
  }
  return 0;
}
